import { Portal, Box, useDisclosure } from '@chakra-ui/react';
import Footer from 'components/footer/FooterAdmin.js';
import Navbar from 'components/navbar/NavbarAdmin.js';
import Sidebar from 'components/sidebar/Sidebar.js';
import SandboxAlert from 'components/notification/SandboxAlert.jsx';
import { SidebarContext } from 'contexts/SidebarContext';
import React, { useState, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import navigation from '../../navigation';
import routes from '../../routes';

export default function Dashboard(props) {
  const { ...rest } = props;
  const [fixed] = useState(false);
  // pinnedOpen: if true, sidebar stays expanded; if false, it auto-hides and opens on hover
  const [toggleSidebar, setToggleSidebar] = useState(() => {
    try {
      const stored = localStorage.getItem('sidebarPinnedOpen');
      return stored ? JSON.parse(stored) : false;
    } catch (e) {
      return false;
    }
  });
  const [hoveringSidebar, setHoveringSidebar] = useState(false);
  const [brandText, setBrandText] = useState('Default Brand Text');
  const [secondary, setSecondary] = useState(false);
  const [message, setMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  useEffect(() => {
    // Check both navigation and routes arrays for the active route
    let brandText = getActiveRoute(navigation);
    if (brandText === 'Default Brand Text') {
      brandText = getActiveRoute(routes);
    }
    setBrandText(brandText);
    setSecondary(getActiveNavbar(navigation));
    setMessage(getActiveNavbarText(navigation));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    try {
      localStorage.setItem('sidebarPinnedOpen', JSON.stringify(toggleSidebar));
    } catch (e) {}
  }, [toggleSidebar]);

  const getRoute = () => {
    return window.location.pathname !== '/admin/full-screen-maps';
  };

  const getActiveRoute = (routes) => {
    let activeRoute = 'Default Brand Text';
    const currentPath = window.location.pathname;
    
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveRoute = getActiveRoute(routes[i].items);
        if (collapseActiveRoute !== activeRoute) {
          return collapseActiveRoute;
        }
      } else if (routes[i].category) {
        let categoryActiveRoute = getActiveRoute(routes[i].items);
        if (categoryActiveRoute !== activeRoute) {
          return categoryActiveRoute;
        }
      } else {
        // Convert path with parameters to regex for matching
        const routePath = routes[i].path.replace(/:\w+/g, '[^/]+');
        const fullPath = routes[i].layout + routePath;
        const pathRegex = new RegExp(`^${fullPath}/?$`);
        
        if (pathRegex.test(currentPath)) {
          return routes[i].name;
        }
      }
    }
    return activeRoute;
  };

  const getActiveNavbar = (routes) => {
    let activeNavbar = false;
    const currentPath = window.location.pathname;
    
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveNavbar = getActiveNavbar(routes[i].items);
        if (collapseActiveNavbar !== activeNavbar) {
          return collapseActiveNavbar;
        }
      } else if (routes[i].category) {
        let categoryActiveNavbar = getActiveNavbar(routes[i].items);
        if (categoryActiveNavbar !== activeNavbar) {
          return categoryActiveNavbar;
        }
      } else {
        // Convert path with parameters to regex for matching
        const routePath = routes[i].path.replace(/:\w+/g, '[^/]+');
        const fullPath = routes[i].layout + routePath;
        const pathRegex = new RegExp(`^${fullPath}/?$`);
        
        if (pathRegex.test(currentPath)) {
          return routes[i].secondary;
        }
      }
    }
    return activeNavbar;
  };

  const getActiveNavbarText = (routes) => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse) {
        let collapseActiveNavbar = getActiveNavbarText(routes[i].items);
        if (collapseActiveNavbar !== activeNavbar) {
          return collapseActiveNavbar;
        }
      } else if (routes[i].category) {
        let categoryActiveNavbar = getActiveNavbarText(routes[i].items);
        if (categoryActiveNavbar !== activeNavbar) {
          return categoryActiveNavbar;
        }
      } else {
        if (
          window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
        ) {
          return routes[i].messageNavbar;
        }
      }
    }
    return activeNavbar;
  };

  const getRoutes = (routeArray) => {
    return routeArray.map((route, key) => {
      if (route.layout === '/admin') {
        return (
          <Route path={`${route.path}`} element={route.component} key={key} />
        );
      }
      if (route.collapse) {
        return getRoutes(route.items);
      } else {
        return null;
      }
    });
  };

  document.documentElement.dir = 'ltr';
  const { onOpen } = useDisclosure();
  document.documentElement.dir = 'ltr';

  return (
    <Box>
      {/* Global Sandbox Alert */}
      <SandboxAlert />
      
      <Box>
        <SidebarContext.Provider
          value={{
            toggleSidebar,
            setToggleSidebar,
            hoveringSidebar,
            setHoveringSidebar,
          }}
        >
          <Sidebar routes={navigation} display="none" {...rest} />
          <Box
            float="right"
            minHeight="100vh"
            height="100%"
            overflow="auto"
            position="relative"
            maxHeight="100%"
            w={{ base: '100%', xl: `calc(100% - ${(toggleSidebar ? 300 : (hoveringSidebar ? 300 : 88))}px)` }}
            maxWidth={{ base: '100%', xl: `calc(100% - ${(toggleSidebar ? 300 : (hoveringSidebar ? 300 : 88))}px)` }}
            transition="all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)"
            transitionDuration=".2s, .2s, .35s"
            transitionProperty="top, bottom, width"
            transitionTimingFunction="linear, linear, ease"
          >
            <Portal>
              <Box>
                <Navbar
                  onOpen={onOpen}
                  logoText={'BlockMap.dev'}
                  brandText={brandText}
                  secondary={secondary}
                  message={message}
                  fixed={fixed}
                  onSearch={setSearchQuery}
                  {...rest}
                />
              </Box>
            </Portal>

            {getRoute() ? (
              <Box
                mx="auto"
                p={{ base: '20px', md: '30px' }}
                pe="20px"
                minH="100vh"
                style={{
                  paddingTop: 'calc(30px + var(--sandbox-alert-height, 0px))'
                }}
              >
                <Routes>
                  {getRoutes(routes)}
                  <Route
                    path="/"
                    element={<Navigate to="/admin/dashboard" replace />}
                  />
                </Routes>
              </Box>
            ) : null}
            <Box>
              <Footer />
            </Box>
          </Box>
        </SidebarContext.Provider>
      </Box>
    </Box>
  );
}
